import { pathOr } from 'ramda';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { createStandaloneToast, theme } from '@frontend/legacy-components';

import { chatDrawer, chatDrawerState } from 'modules/common/chat/data/actions';
import {
  CHANNEL_TYPES,
  CHAT_DRAWER_STATE,
} from 'modules/common/chat/data/constants';
import {
  getPartMasterVersion,
  getTechDataPackageRootUuid,
} from 'modules/common/chat/data/reducer';
import { entitiesStore } from 'modules/common/chat/entities/actions';
import { getChannelById } from 'modules/common/chat/entities/reducer';

import partLibraryService from 'services/partLibraryService';

import {
  createChannel,
  createShares,
  deleteShare,
  deleteSharesForChannel,
  initShareSettings,
  types,
  updateChannelSettings,
  updateShare,
  updateSharesForChannel,
} from './actions';
import { getChannelId, getShareExpirationChanges } from './reducer';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  yield takeLatest(types.CREATE_CHANNEL_REQUEST, watchCreateChannel);
  yield takeLatest(types.CREATE_SHARES_REQUEST, watchCreateShares);
  yield takeEvery(types.DELETE_SHARE_REQUEST, watchDeleteShare);
  yield takeLatest(
    types.DELETE_SHARES_FOR_CHANNEL_REQUEST,
    watchDeleteSharesForChannel,
  );
  yield takeLatest(
    types.UPDATE_CHANNEL_SETTINGS_REQUEST,
    watchUpdateChannelSettings,
  );
  yield takeLatest(types.CHANNEL_SETTINGS_MODAL_OPEN, watchInitShareSettings);
  yield takeLatest(
    types.TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_OPEN,
    watchInitShareSettings,
  );

  yield takeLatest(types.NEW_CHANNEL_MODAL_OPEN, watchInitShareSettings);

  yield takeLatest(types.UPDATE_SHARE_REQUEST, watchUpdateShare);
  yield takeLatest(
    types.UPDATE_SHARES_FOR_CHANNEL_REQUEST,
    watchUpdateSharesForChannelRequest,
  );
}

function* watchCreateChannel(action) {
  try {
    const { sendInvitesSeparately, sharedWithEmails } = action.payload;
    if (sendInvitesSeparately && sharedWithEmails.length > 1) {
      yield call(createMultipleChannels, action.payload);
    } else {
      const response = yield call(createOneChannel, action.payload);
      const { type } = action.payload;
      yield put(
        chatDrawerState.set({
          activeChannelId: response.id,
          activeChannelType: type,
          chatDrawerState:
            type === CHANNEL_TYPES.MSC || type === CHANNEL_TYPES.SECONDARY
              ? CHAT_DRAWER_STATE.EXTERNAL
              : CHAT_DRAWER_STATE.TEAM,
        }),
      );
      yield put(chatDrawer.open());
    }
  } catch (e) {
    yield put(createChannel.error(e));
  }
}

function* createMultipleChannels({
  accessToAllPartFiles,
  accessToPrimaryFile,
  emailSubjectLine,
  message,
  name,
  permissionToDownloadFiles,
  permissionToUploadFiles,
  sharedWithEmails,
  supportingFilesAccess,
  type,
  expirationDate,
}) {
  try {
    for (const email of sharedWithEmails) {
      yield call(createOneChannel, {
        accessToAllPartFiles,
        accessToPrimaryFile,
        emailSubjectLine,
        message,
        name: `${name} (${email})`,
        permissionToDownloadFiles,
        permissionToUploadFiles,
        sharedWithEmails: [email],
        supportingFilesAccess,
        type,
        expirationDate,
      });
    }
  } catch (e) {
    yield put(createChannel.error(e));
  }
}

function* createOneChannel({
  accessToAllPartFiles,
  accessToPrimaryFile,
  emailSubjectLine,
  message,
  name,
  permissionToDownloadFiles,
  permissionToUploadFiles,
  sharedWithEmails,
  supportingFilesAccess,
  type,
  expirationDate,
}) {
  try {
    const techDataPackageRootUuid = yield select(getTechDataPackageRootUuid);
    const partMasterVersion = yield select(getPartMasterVersion);
    const response = yield partLibraryService.channels.create(
      techDataPackageRootUuid,
      {
        accessToAllPartFiles,
        accessToPrimaryFile,
        name,
        partMasterVersion,
        permissionToDownloadFiles,
        permissionToUploadFiles,
        supportingFilesAccess,
        type,
      },
    );

    yield put(entitiesStore.receiveChannel(response));
    yield put(createChannel.success());

    if ((sharedWithEmails || []).length) {
      yield put(
        createShares.request({
          channelId: response.id,
          emailAddresses: sharedWithEmails,
          emailSubjectLine,
          message,
          expirationDate,
        }),
      );
    }

    return response;
  } catch (e) {
    yield put(createChannel.error(e));
  }
}

function* watchCreateShares(action) {
  try {
    const {
      channelId,
      emailAddresses,
      emailSubjectLine,
      expirationDate,
      message,
    } = action.payload;
    yield all(
      emailAddresses.map((emailAddress) =>
        call(createShare, {
          channelId,
          emailAddress,
          emailSubjectLine,
          message,
          expirationDate,
        }),
      ),
    );
    yield put(createShares.success());
  } catch (e) {
    yield put(createShares.error(e));
  }
}

function* createShare({
  channelId,
  emailAddress,
  emailSubjectLine,
  expirationDate,
  message,
}) {
  try {
    const partMasterVersion = yield select(getPartMasterVersion);
    const response = yield partLibraryService.shares.create(channelId, {
      emailAddress,
      emailSubjectLine,
      expirationDate,
      message,
      partMasterVersion,
    });
    if (pathOr(false, ['channelId'], response)) {
      // share will not be created if it is for team member
      yield put(entitiesStore.receiveShare(response));
    }
  } catch (e) {
    toast({
      title: `Failed to share with ${emailAddress}.`,
      description: e.message,
      status: 'error',
    });
    throw e;
  }
}

function* watchDeleteShare(action) {
  try {
    const { shareId } = action.payload;
    yield partLibraryService.shares.delete(shareId);
    yield put(deleteShare.success());
  } catch (e) {
    yield put(deleteShare.error(e));
  }
}

function* watchUpdateShare(action) {
  try {
    const { shareId, expirationDate } = action.payload;
    yield partLibraryService.shares.patch(shareId, { expirationDate });
    yield put(updateShare.success());
  } catch (e) {
    yield put(updateShare.error(e));
  }
}

function* watchUpdateSharesForChannelRequest(action) {
  try {
    const { channelId, expirationDate } = action.payload;
    const channel = yield select(getChannelById, channelId);
    yield all(
      channel.shares
        .filter((share) => share.accessRevokedOn == null)
        .map((share) =>
          call(partLibraryService.shares.patch, share.id, {
            expirationDate,
          }),
        ),
    );
    yield put(updateSharesForChannel.success());
  } catch (e) {
    yield put(updateSharesForChannel.error(e));
  }
}

function* watchDeleteSharesForChannel(action) {
  try {
    const { channelId } = action.payload;
    const channel = yield select(getChannelById, channelId);

    yield all(
      channel.shares
        .filter((share) => share.accessRevokedOn == null)
        .map((share) => call(partLibraryService.shares.delete, share.id)),
    );
    yield put(deleteSharesForChannel.success());
  } catch (e) {
    yield put(deleteSharesForChannel.error(e));
  }
}

function* watchInitShareSettings() {
  try {
    const techDataPackageRootUuid = yield select(getTechDataPackageRootUuid);
    const channelId = yield select(getChannelId);
    const [technicalDataPackage, channel] = yield all([
      partLibraryService.getTechnicalDataPackage(techDataPackageRootUuid),
      partLibraryService.channels.get(channelId),
    ]);
    yield put(entitiesStore.receiveChannel(channel));
    yield put(initShareSettings.success({ technicalDataPackage }));
  } catch (error) {
    yield put(initShareSettings.error(error));
  }
}

function* watchUpdateChannelSettings(action) {
  try {
    const channelId = yield select(getChannelId);
    const {
      accessToAllPartFiles,
      accessToPrimaryFile,
      name,
      supportingFilesAccess,
      permissionToUploadFiles,
      permissionToDownloadFiles,
    } = action.payload;

    const shareExpirationChanges = yield select(getShareExpirationChanges);
    yield all(
      Object.keys(shareExpirationChanges).map((shareId) =>
        call(watchUpdateShare, {
          payload: {
            shareId,
            expirationDate: shareExpirationChanges[shareId],
          },
        }),
      ),
    );

    const response = yield partLibraryService.channels.patch(channelId, {
      accessToAllPartFiles,
      accessToPrimaryFile,
      name,
      supportingFilesAccess,
      permissionToUploadFiles,
      permissionToDownloadFiles,
    });
    yield put(entitiesStore.receiveChannel(response));
    yield put(updateChannelSettings.success(response));
  } catch (e) {
    yield put(updateChannelSettings.error(e));
  }
}
